"use client";

import { Api } from "@/app/api";
import type {
	CreatePromoCodeDto,
	PromoCode,
	PromoCodeResponse,
	UpdatePromoCodeDto,
} from "@/types";
import type {
	PromoCodePackageTier,
	ValidatePromoCodeResponse,
} from "@/lib/promo-code";

export type ValidatePromoCodeBody = {
	code: string;
	currency?: string;
	email?: string;
	packageAmount?: number;
	licenseAmount?: number;
	hostingAmount?: number;
	planAmount?: number;
	packageTiers?: PromoCodePackageTier[];
	orderAmount?: number;
	/** Codes already on the cart — enforces stackableWithOtherPromoCodes. */
	otherAppliedCodes?: string[];
};

export const PromoCodeApiInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		validatePromoCode: builder.mutation<ValidatePromoCodeResponse, ValidatePromoCodeBody>({
			query: (body) => ({
				url: "/promo-codes/validate",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
		}),

		createPromoCode: builder.mutation<PromoCode, CreatePromoCodeDto>({
			query: (body) => ({
				url: "/promo-codes",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["PromoCodes"],
		}),

		updatePromoCode: builder.mutation<
			PromoCode,
			{ id: string } & UpdatePromoCodeDto
		>({
			query: ({ id, ...body }) => ({
				url: `/promo-codes/${id}`,
				method: "PATCH",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_r, _e, { id }) => [{ type: "PromoCodes", id }, "PromoCodes"],
		}),

		deletePromoCode: builder.mutation<{ deleted: true }, string>({
			query: (id) => ({
				url: `/promo-codes/${id}`,
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_r, _e, id) => [{ type: "PromoCodes", id }, "PromoCodes"],
		}),

		getPromoCodeById: builder.query<PromoCode, string>({
			query: (id) => ({
				url: `/promo-codes/${id}`,
				headers: { "Content-Type": "application/json" },
			}),
			providesTags: (_r, _e, id) => [{ type: "PromoCodes", id }],
		}),

		searchPromoCodes: builder.query<
			PromoCodeResponse,
			{ searchQuery?: string; sortBy?: string; page: number; limit: number }
		>({
			query: ({ searchQuery, sortBy, page, limit }) => ({
				url: "/promo-codes",
				params: {
					page,
					limit,
					search: searchQuery,
					sortBy: sortBy ?? "createdAt:desc",
				},
				headers: { "Content-Type": "application/json" },
			}),
			providesTags: ["PromoCodes"],
		}),
	}),
});

export const {
	useValidatePromoCodeMutation,
	useCreatePromoCodeMutation,
	useUpdatePromoCodeMutation,
	useDeletePromoCodeMutation,
	useGetPromoCodeByIdQuery,
	useSearchPromoCodesQuery,
} = PromoCodeApiInjection;
